import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import type { TextFieldProps as AriaTextFieldProps } from 'react-aria-components';
import { Provider, TextField as AriaTextField } from 'react-aria-components';

import { IGNORE_PASSWORD_MANAGERS } from '@infinex/ui-constants';
import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useStyleProps } from '@infinex/ui-styles';

import type { CommonFieldProps } from '../field';
import {
  Adornment,
  FieldContext,
  FieldLabel,
  HelpText,
  Input,
  InputGroup,
  fieldRootStyles,
} from '../field';

export type TextFieldProps = CommonFieldProps & {
  /** Element to display before the input. */
  startElement?: ReactNode;
  /** Element to display after the input. */
  endElement?: ReactNode;
  /** Whether to ignore password managers. */
  ignorePasswordManagers?: boolean;
} & Omit<AriaTextFieldProps, 'children' | 'isInvalid' | 'className' | 'style'> &
  BaseStyleProps &
  UnsafeStyleProps;

// ^ RAC omissions:
// - children, className, and style: render props not supported
// - isInvalid: derived from `errorMessage` prop

/**
 * Text fields allow users to input text with a keyboard. Use when the expected
 * input is a single line of text.
 *
 * [Taurus docs: `TextField`](https://ui-sb.app.infinex.dev/?path=/docs/components-textfield--docs)
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(props, forwardedRef) {
    const {
      endElement,
      errorMessage,
      hint,
      label,
      prominence,
      size = 'medium',
      startElement,
      ignorePasswordManagers,
      ...otherProps
    } = props;

    const { className, style } = useStyleProps(props);

    return (
      <AriaTextField
        className={cn(fieldRootStyles(), className)}
        style={style}
        isInvalid={!!errorMessage || undefined}
        {...otherProps}
      >
        {(renderProps) => (
          <Provider values={[[FieldContext, { size, ...renderProps }]]}>
            {label ? <FieldLabel>{label}</FieldLabel> : null}

            <InputGroup prominence={prominence} role="presentation">
              {startElement && (
                <Adornment placement="start">{startElement}</Adornment>
              )}

              <Input
                ref={forwardedRef}
                {...(ignorePasswordManagers ? IGNORE_PASSWORD_MANAGERS : {})}
              />

              {endElement && (
                <Adornment placement="end">{endElement}</Adornment>
              )}
            </InputGroup>

            <HelpText description={hint}>{errorMessage}</HelpText>
          </Provider>
        )}
      </AriaTextField>
    );
  }
);
